<template>
  <div
    :class="[
      'input-v3 tw:relative tw:inline-flex tw:items-center tw:rounded-[8px] tw:border tw:transition-all tw:duration-150',
      sizeClass,
      stateClasses,
      { 'skeleton-play': skeleton },
    ]"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <!-- Left Icon -->
    <IconGoogle
      v-if="icon"
      :name="icon"
      :size="iconSize"
      :color="iconColor"
      :class="[
        'tw:shrink-0',
        iconClickable ? 'tw:cursor-pointer hover:tw:opacity-70' : 'tw:cursor-default',
      ]"
      :style="iconPaddingLeft"
      data-testid="input-v3-left-icon"
      @click="iconClickable && emit('iconClick')"
    />

    <!-- Native Input -->
    <input
      :id="id"
      ref="inputRef"
      v-model="internalValue"
      v-maska="maskResolved"
      :data-maska="maskArrayResolved"
      :class="[
        'tw:flex-1 tw:min-w-0 tw:bg-transparent tw:outline-none tw:border-none tw:text-(--fontcolor)',
        'tw:placeholder-(--placeholder-color)',
        inputPaddingClass,
        { 'tw:cursor-not-allowed tw:opacity-50': disabled },
      ]"
      :placeholder="placeholder"
      :type="type"
      :disabled="disabled"
      :readonly="readonly"
      :maxlength="maxlength"
      :min="min"
      :max="max"
      :autocomplete="autocomplete"
      :name="name"
      :data-testid="id"
      @focus="focused = true"
      @blur="focused = false"
      @keypress.enter="emit('enter')"
    />

    <!-- Right Icon -->
    <IconGoogle
      v-if="rightIcon"
      :name="rightIcon"
      :size="iconSize"
      :color="iconColor"
      :class="[
        'tw:shrink-0',
        rightIconClickable ? 'tw:cursor-pointer hover:tw:opacity-70' : 'tw:cursor-default',
      ]"
      :style="iconPaddingRight"
      data-testid="input-v3-right-icon"
      @click="rightIconClickable && emit('rightIconClick')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import IconGoogle from '../IconGoogle/IconGoogle.vue'

const props = withDefaults(defineProps<{
  modelValue?: string | number
  id?: string
  name?: string
  placeholder?: string
  type?: string
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  readonly?: boolean
  skeleton?: boolean
  error?: boolean
  maxlength?: number | string
  min?: number | string
  max?: number | string
  autocomplete?: string
  icon?: string
  iconClickable?: boolean
  rightIcon?: string
  rightIconClickable?: boolean
  mask?: string | string[]
}>(), {
  modelValue: undefined,
  id: undefined,
  name: undefined,
  placeholder: undefined,
  type: 'text',
  size: 'md',
  disabled: false,
  readonly: false,
  skeleton: false,
  error: false,
  maxlength: undefined,
  min: undefined,
  max: undefined,
  autocomplete: undefined,
  icon: undefined,
  iconClickable: false,
  rightIcon: undefined,
  rightIconClickable: false,
  mask: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | undefined]
  'iconClick': []
  'rightIconClick': []
  'enter': []
}>()

const inputRef = ref<HTMLInputElement | null>(null)
const focused = ref(false)
const hovered = ref(false)

const internalValue = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

// --- Mask ---
const maskResolved = computed(() => props.mask ?? undefined)
const maskArrayResolved = computed(() => {
  if (Array.isArray(props.mask)) return JSON.stringify(props.mask)
  return undefined
})

// --- Size classes (matching ButtonPlayV2 heights) ---
const sizeClass = computed(() => ({
  sm: 'tw:h-8 tw:text-xs',
  md: 'tw:h-[46px] tw:text-sm',
  lg: 'tw:h-[52px] tw:text-base',
}[props.size]))

const iconSize = computed(() => ({
  sm: '14px',
  md: '18px',
  lg: '20px',
}[props.size]))

const iconPaddingLeft = computed(() => ({
  paddingLeft: { sm: '8px', md: '12px', lg: '14px' }[props.size],
}))

const iconPaddingRight = computed(() => ({
  paddingRight: { sm: '8px', md: '12px', lg: '14px' }[props.size],
}))

const inputPaddingClass = computed(() => {
  const base = {
    sm: 'tw:px-2',
    md: 'tw:px-3',
    lg: 'tw:px-4',
  }[props.size]
  // Reduce padding on the side that has an icon
  const left = props.icon ? 'tw:pl-1.5' : ''
  const right = props.rightIcon ? 'tw:pr-1.5' : ''
  return [base, left, right].filter(Boolean).join(' ')
})

const iconColor = computed(() => {
  if (props.error) return 'var(--feedback-error-500)'
  if (focused.value) return 'var(--fontcolor)'
  return 'var(--input-border-color)'
})

// --- State classes ---
const stateClasses = computed(() => {
  if (props.disabled) return 'tw:border-(--input-border-color) tw:bg-(--bg-input) tw:opacity-60 tw:cursor-not-allowed'
  if (props.error) return 'tw:border-(--feedback-error-500) tw:bg-(--bg-input)'
  if (focused.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input) tw:ring-2 tw:ring-(--fontcolor)/10'
  if (hovered.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input)'
  return 'tw:border-(--input-border-color) tw:bg-(--bg-input)'
})

defineExpose({ inputRef, focus: () => inputRef.value?.focus() })
</script>

<style scoped>
.input-v3.skeleton-play > * {
  opacity: 0;
}

.input-v3 input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px var(--bg-input) inset !important;
  -webkit-text-fill-color: var(--fontcolor) !important;
}

.input-v3 input::placeholder {
  color: var(--placeholder-color) !important;
}
</style>
